import { useCallback, useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ items, index, onClose, onIndex }) {
  const touchX = useRef(null);
  const item = items[index];
  const prev = useCallback(() => onIndex((index - 1 + items.length) % items.length), [index, items.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % items.length), [index, items.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, prev, next]);

  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0F2744]/95 backdrop-blur-md flex flex-col animate-in fade-in duration-200"
      onClick={onClose} onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}
      role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="lightbox">
      <div className="flex items-center justify-between px-4 sm:px-6 h-16 text-slate-200 text-sm">
        <span data-testid="lightbox-counter">{index + 1} / {items.length}</span>
        <button onClick={onClose} className="h-10 w-10 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label="Close" data-testid="lightbox-close"><X className="h-6 w-6" /></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-4 sm:px-20 min-h-0">
        <button onClick={(e) => { e.stopPropagation(); prev(); }} className="hidden sm:flex absolute left-4 h-12 w-12 rounded-full bg-white/10 hover:bg-amber-500 hover:text-[#0F2744] text-white items-center justify-center transition-colors" aria-label="Previous photo" data-testid="lightbox-prev"><ChevronLeft className="h-7 w-7" /></button>
        <img key={item.id} src={item.url} alt={item.caption} onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full object-contain rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-testid="lightbox-image" />
        <button onClick={(e) => { e.stopPropagation(); next(); }} className="hidden sm:flex absolute right-4 h-12 w-12 rounded-full bg-white/10 hover:bg-amber-500 hover:text-[#0F2744] text-white items-center justify-center transition-colors" aria-label="Next photo" data-testid="lightbox-next"><ChevronRight className="h-7 w-7" /></button>
      </div>
      <div className="px-6 py-5 text-center">
        {item.caption && <p className="text-white font-semibold" data-testid="lightbox-caption">{item.caption}</p>}
        <p className="text-amber-400 text-xs uppercase tracking-widest mt-1">{item.category}</p>
        <p className="text-slate-400 text-xs mt-2 sm:hidden">Swipe to browse</p>
      </div>
    </div>
  );
}
